import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

const BETTERPLACE_PROJECT_ID = "157400";

function PrivateDonation() {
  const { t, i18n } = useTranslation();

  const langParam = useMemo(() => {
    if (i18n.language?.startsWith("de")) return "de";
    return "en";
  }, [i18n.language]);

  const widgetSrc = `https://project-widget.betterplace.org/projects/${BETTERPLACE_PROJECT_ID}?l=${langParam}`;
  const projectPageUrl = `https://www.betterplace.org/${langParam}/projects/${BETTERPLACE_PROJECT_ID}`;

  return (
    <section
      className="relative z-10 bg-white pb-14 pt-12 transition-colors dark:bg-slate-800 md:pb-16 md:pt-14"
      id="private-donation"
      aria-labelledby="private-donation-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="mb-10 text-center md:mb-12">
          <h2
            id="private-donation-heading"
            className="relative mb-5 inline-block text-4xl font-semibold tracking-tight text-primary dark:text-primary-light md:text-5xl"
          >
            {t("donations.private.title")}
            <span
              className="absolute -bottom-2.5 left-1/2 h-0.5 w-16 -translate-x-1/2 rounded-full bg-primary dark:bg-primary-light"
              aria-hidden
            />
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-pretty text-lg leading-relaxed text-slate-600 dark:text-slate-400">
            {t("donations.private.description")}
          </p>
        </header>

        <div className="mx-auto max-w-md">
          <div className="overflow-hidden rounded-2xl border border-slate-200 border-l-4 border-l-primary bg-white shadow-sm dark:border-slate-700 dark:border-l-primary-light dark:bg-slate-800">
            <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 dark:border-slate-700 sm:px-5">
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary-light/15"
                aria-hidden
              >
                <i className="fas fa-heart text-lg text-primary dark:text-primary-light" />
              </div>
              <p className="min-w-0 text-left text-sm font-medium leading-snug text-slate-700 dark:text-slate-200">
                {t("donations.private.widget_caption")}
              </p>
            </div>
            <div className="flex justify-center bg-slate-50/80 px-4 py-6 dark:bg-slate-900/40 sm:px-6 sm:py-8">
              <iframe
                title={t("donations.private.widget_iframe_title")}
                src={widgetSrc}
                width={280}
                height={320}
                className="max-w-full border-0"
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>

            <div className="border-t border-slate-200 bg-white px-4 py-3 dark:border-slate-600 dark:bg-slate-800 sm:px-5 sm:py-3.5">
              <a
                href={projectPageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-center text-sm font-medium text-primary transition-colors hover:bg-primary/5 dark:text-primary-light dark:hover:bg-primary-light/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 focus-visible:ring-offset-2 dark:focus-visible:ring-primary-light/35 dark:focus-visible:ring-offset-slate-800"
                aria-label={t("donations.private.open_betterplace_aria")}
              >
                {t("donations.private.open_betterplace")}
                <i
                  className="fas fa-external-link-alt text-xs opacity-80"
                  aria-hidden
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default PrivateDonation;
